import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { listProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminUploadImage, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ImageOff, UploadCloud, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { CertificateUpload } from "@/components/CertificateUpload";

const CATS = ["pesticides", "herbicides", "fertilizers", "seeds", "tools", "organic_pesticides", "organic_produce", "residue_free"];

const emptyForm = {
  name_en: "", name_hi: "", description_en: "", description_hi: "",
  category: "fertilizers", price: "", unit: "1 kg", stock: 100, image_path: null, certificate_path: null,
};

const AdminProductsPage = () => {
  const { t, lang, adminToken } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    listProducts(null, true).catch(() => listProducts()).then((data) => setProducts(data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    // include_inactive=true for admin
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?include_inactive=true`)
      .then((r) => r.json())
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name_en: p.name_en, name_hi: p.name_hi,
      description_en: p.description_en, description_hi: p.description_hi,
      category: p.category, price: p.price, unit: p.unit, stock: p.stock,
      image_path: p.image_path, certificate_path: p.certificate_path || null,
    });
    setDialogOpen(true);
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const res = await adminUploadImage(adminToken, file);
      setForm((p) => ({ ...p, image_path: res.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name_en || !form.name_hi || !form.price) {
      toast.error("Fill required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10) || 0,
      };
      if (editing) {
        const updated = await adminUpdateProduct(adminToken, editing.id, payload);
        setProducts((prev) => prev.map((p) => p.id === editing.id ? updated : p));
        toast.success("Product updated");
      } else {
        const created = await adminCreateProduct(adminToken, payload);
        setProducts((prev) => [created, ...prev]);
        toast.success("Product added");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminDeleteProduct(adminToken, confirmDelete.id);
      setProducts((prev) => prev.map((p) => p.id === confirmDelete.id ? { ...p, is_active: false } : p));
      toast.success("Product removed");
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#1E293B]">{t("manageProducts")}</h1>
          <p className="text-sm text-slate-500">Add, edit or remove products from your catalog</p>
        </div>
        <Button onClick={openAdd} data-testid="add-product-btn" className="rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> {t("addProduct")}
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 bg-white border border-[#E2E8F0] rounded-md animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E2E8F0] rounded-md">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 mb-4">{t("noProducts")}</p>
          <Button onClick={openAdd} className="rounded-md bg-[#2D5A27] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> {t("addProduct")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => {
            const img = fileUrl(p.image_path);
            const name = lang === "hi" ? p.name_hi : p.name_en;
            return (
              <div
                key={p.id}
                className={`bg-white border rounded-md overflow-hidden ${p.is_active ? "border-[#E2E8F0]" : "border-rose-200 opacity-60"}`}
                data-testid={`admin-product-card-${p.id}`}
              >
                <div className="flex gap-3 p-3">
                  <div className="w-20 h-20 rounded-md bg-[#EFEBE2] overflow-hidden shrink-0">
                    {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageOff className="w-5 h-5" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t(p.category)}</p>
                    <h3 className={`font-heading font-semibold text-sm text-[#1E293B] line-clamp-2 ${lang === "hi" ? "font-devanagari" : ""}`}>{name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{p.unit} · Stock: {p.stock}</p>
                    <p className="font-heading font-bold text-[#2D5A27] mt-1">₹{p.price.toFixed(0)}</p>
                  </div>
                </div>
                <div className="border-t border-[#E2E8F0] p-2 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="flex-1 rounded-md">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> {t("edit")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(p)} data-testid={`delete-product-${p.id}`} className="rounded-md text-rose-600 hover:bg-rose-50 border-rose-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? t("editProduct") : t("addProduct")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">{t("productName")}</Label>
                <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="prod-name-en" className="mt-1 h-11 bg-white" required />
              </div>
              <div>
                <Label className="text-sm">{t("productNameHi")}</Label>
                <Input value={form.name_hi} onChange={(e) => setForm({ ...form, name_hi: e.target.value })} data-testid="prod-name-hi" className="mt-1 h-11 bg-white font-devanagari" required />
              </div>
              <div>
                <Label className="text-sm">{t("description")}</Label>
                <Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="prod-desc-en" rows={2} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-sm">{t("descriptionHi")}</Label>
                <Textarea value={form.description_hi} onChange={(e) => setForm({ ...form, description_hi: e.target.value })} data-testid="prod-desc-hi" rows={2} className="mt-1 bg-white font-devanagari" />
              </div>
              <div>
                <Label className="text-sm">{t("category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="prod-category" className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CATS.map((c) => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">{t("unit")}</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="prod-unit" className="mt-1 h-11 bg-white" />
              </div>
              <div>
                <Label className="text-sm">{t("price")}</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" className="mt-1 h-11 bg-white" required />
              </div>
              <div>
                <Label className="text-sm">{t("stock")}</Label>
                <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="prod-stock" className="mt-1 h-11 bg-white" />
              </div>
            </div>

            {/* Image upload */}
            <div>
              <Label className="text-sm">{t("productImage")}</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-24 h-24 rounded-md border-2 border-dashed border-[#E2E8F0] overflow-hidden bg-[#EFEBE2] flex items-center justify-center shrink-0">
                  {form.image_path ? (
                    <img src={fileUrl(form.image_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    id="prod-image-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => upload(e.target.files?.[0])}
                    data-testid="prod-image-input"
                    className="hidden"
                  />
                  <label
                    htmlFor="prod-image-input"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#2D5A27] text-[#2D5A27] text-sm font-semibold cursor-pointer hover:bg-[#EFEBE2]"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {uploading ? "Uploading..." : t("upload")}
                  </label>
                  <p className="text-xs text-slate-500 mt-1.5">{t("imageOptional")}</p>
                </div>
              </div>
            </div>

            <CertificateUpload
              path={form.certificate_path}
              onChange={(v) => setForm((p) => ({ ...p, certificate_path: v }))}
            />

            <div className="flex gap-2 justify-end pt-2 border-t border-[#E2E8F0]">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-md">
                {t("cancel")}
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-product-btn" className="rounded-md bg-[#2D5A27] hover:bg-[#21421D] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this product?</AlertDialogTitle>
            <AlertDialogDescription>
              The product will be hidden from the catalog. This can be reversed by re-adding it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProductsPage;
